// Autonomous Coach API client (Phase 4). Tracks the active PLAN session so the
// app can forward the SAME trustworthy UI events (route_entered/builder_opened/
// widget_added) to the plan runtime. No completion flag is ever sent — the
// server evaluates each step's predicate and decides advance/completion.
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/coach/autonomous`;

function csrf() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

async function call(path, method = 'GET', body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(typeof data.detail === 'string' ? data.detail : `autonomous ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return data;
}

// Map an app route path → the coachable routeId the plan runtime expects. Only
// the routes the seed capabilities reference are mapped.
function routeIdFor(path) {
  const p = String(path || '');
  if (p.startsWith('/reports')) return 'reports.home';
  if (p.startsWith('/dashboards')) return 'dashboards.home';
  return null;
}

// The active PLAN tracker is persisted to localStorage so a hard page reload
// does NOT lose the in-flight session — the durable session (and its undo
// eligibility) lives server-side in Mongo, and we only need {session_id, nonce,
// seq} to keep talking to it. Cleared by end()/cancel() or a new plan.
const LS_KEY = 'coach_autonomous_plan';
function loadPlan() {
  try { const raw = localStorage.getItem(LS_KEY); return raw ? JSON.parse(raw) : null; }
  catch (e) { return null; }
}
function savePlan() {
  try {
    if (plan && plan.session_id) localStorage.setItem(LS_KEY, JSON.stringify(plan));
    else localStorage.removeItem(LS_KEY);
  } catch (e) { /* storage unavailable — in-memory tracker still works this session */ }
}

let plan = loadPlan(); // { session_id, nonce, seq }
const changeListeners = new Set();
const eventListeners = new Set();
export function onPlanChange(fn) { changeListeners.add(fn); return () => changeListeners.delete(fn); }
export function onPlanEvent(fn) { eventListeners.add(fn); return () => eventListeners.delete(fn); }
function notify() { changeListeners.forEach((f) => f(plan)); }
function notifyEvent(d) { eventListeners.forEach((f) => f(d)); }

export const autonomousApi = {
  getActive: () => plan,
  // Keep the client's optimistic seq in sync with the server's authoritative
  // event_seq (used when re-hydrating a persisted plan after a reload).
  reconcile: (seq) => { if (plan && typeof seq === 'number' && seq >= (plan.seq || 0)) { plan.seq = seq; savePlan(); } },
  // Preview: resolve + validate a plan WITHOUT starting a session (route left
  // empty so all permitted capabilities are eligible regardless of the page).
  preview: (question) => call('/plan', 'POST', { question, route: '' }),
  async start(question, confirm) {
    const r = await call('/session', 'POST', { question, route: '', confirm: !!confirm });
    if (r.session_id) { plan = { session_id: r.session_id, nonce: r.nonce, seq: r.seq || 0 }; savePlan(); notify(); }
    return r;
  },
  step: (id) => call(`/session/${id}/step`),
  control: (id, action) => call(`/session/${id}/control`, 'POST', { action }),
  confirm: (id, stepId) => call(`/session/${id}/confirm`, 'POST', { stepId }),
  undo: (id, stepId) => call(`/session/${id}/undo`, 'POST', { stepId }),
  async forwardEvent(type, semanticTargetId, state) {
    if (!plan) return null;
    plan.seq += 1; // optimistic strictly-increasing sequence (server is authoritative)
    savePlan();
    const body = { type, nonce: plan.nonce, seq: plan.seq, targetId: semanticTargetId || null };
    const st = {};
    if (state && typeof state.route === 'string') {
      const rid = routeIdFor(state.route);
      if (rid) st.routeId = rid;
    }
    if (state && typeof state.routeId === 'string') st.routeId = state.routeId;
    if (Object.keys(st).length) body.state = st;
    let r = null;
    try { r = await call(`/session/${plan.session_id}/event`, 'POST', body); }
    catch (e) { return null; }
    notifyEvent(r);
    // NOTE: keep the plan tracker after completion so the user can still UNDO a
    // reversible final step (undo reopens it and needs the same nonce/seq).
    // The tracker is cleared only by end()/cancel() or when a new plan starts.
    return r;
  },
  end() { plan = null; savePlan(); notify(); },
};
